import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  useTheme,
  Tooltip,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import FlagIcon from "@mui/icons-material/Flag";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AssignmentIcon from "@mui/icons-material/Assignment";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import DownloadIcon from "@mui/icons-material/Download";
import { AuditorStatusType, AdminStatusType, AuditStatus } from "../../../../../utils/enums";

const ControlActionButtons = ({
  currentStatus,
  redFlag,
  statusLoading,
  isSubmittingStatus,
  isSubmittingRedFlag,
  onStatusUpdate,
  onRedFlagToggle,
  onRemoveStatus,
  onCommentsClick,
  onEvidenceRequestClick,
  onReadyForAuditClick,
  onDownloadEvidenceClick,
  isDownloadingEvidence = false,
  isAuditorView = false,
  commentsCount = 0,
  audit,
}) => {
  const theme = useTheme();

  // Check if audit is completed or archived
  const isAuditClosed = audit?.audit_status === AuditStatus.COMPLETED || audit?.audit_status === AuditStatus.ARCHIVED;
  const getAuditClosedMessage = (action) => {
    if (audit?.audit_status === AuditStatus.COMPLETED) {
      return `${action} is disabled because the audit has been completed`;
    }
    return `${action} is disabled because the audit has been archived`;
  };

  // For Auditor: Check if approved button should be disabled
  // Disable when: red flag is set OR control is not ready for audit OR audit is closed
  const isApprovedDisabled = isAuditorView && (
    isAuditClosed ||
    redFlag ||
    (currentStatus !== AdminStatusType.READY_FOR_AUDIT && currentStatus !== AuditorStatusType.APPROVED)
  );

  // For Auditor: Check if red flag button should be disabled
  // Disable when: status is approved OR control is not ready for audit OR audit is closed
  const isRedFlagDisabled = isAuditorView && (
    isAuditClosed ||
    currentStatus === AuditorStatusType.APPROVED ||
    (currentStatus !== AdminStatusType.READY_FOR_AUDIT && currentStatus !== AuditorStatusType.APPROVED)
  );

  // Handle approved button click - toggle between approved and not approved
  const handleApprovedClick = () => {
    if (currentStatus === AuditorStatusType.APPROVED) {
      onRemoveStatus?.();
    } else {
      onStatusUpdate(AuditorStatusType.APPROVED);
    }
  };

  if (statusLoading) {
    return (
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2.5,
            py: 0.875,
            borderRadius: 1,
            border: `1px solid ${theme.customColors.border.main}`,
            backgroundColor: theme.customColors.background.main,
          }}
        >
          <CircularProgress size={16} sx={{ color: theme.customColors.text.secondary }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
      {/* Only show status buttons for auditors */}
      {isAuditorView && (
        <>
          {/* Approved Button */}
          <Tooltip
            title={
              isApprovedDisabled
                ? (isAuditClosed
                    ? getAuditClosedMessage("Mark Approved")
                    : redFlag
                      ? "Cannot approve while red flag is set"
                      : "Control must be ready for audit before approval")
                : ""
            }
            arrow
          >
            <span>
              <Button
                variant="outlined"
                disabled={isSubmittingStatus || isApprovedDisabled}
                onClick={handleApprovedClick}
                startIcon={
                  isSubmittingStatus && currentStatus === AuditorStatusType.APPROVED ? (
                    <CircularProgress size={16} sx={{ color: "inherit" }} />
                  ) : (
                    <CheckCircleIcon />
                  )
                }
                sx={{
                  borderRadius: 1,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  px: 2.5,
                  py: 0.875,
                  minWidth: 130,
                  ...(currentStatus === AuditorStatusType.APPROVED
                    ? {
                        borderColor: "#10B981",
                        bgcolor: "rgba(16, 185, 129, 0.12)",
                        color: "#059669",
                        "&:hover": {
                          borderColor: "#059669",
                          bgcolor: "rgba(16, 185, 129, 0.2)",
                        },
                        "&:disabled": {
                          bgcolor: "#D1D5DB !important",
                          color: "#9CA3AF !important",
                          borderColor: "#D1D5DB !important",
                          boxShadow: "none !important",
                        },
                      }
                    : {
                        borderColor: theme.customColors.border.main,
                        color: theme.customColors.text.strong,
                        "&:hover": {
                          borderColor: "#10B981",
                          bgcolor: "rgba(16, 185, 129, 0.08)",
                        },
                        "&:disabled": {
                          bgcolor: "#D1D5DB !important",
                          borderColor: "#D1D5DB !important",
                          color: "#9CA3AF !important",
                          boxShadow: "none !important",
                        },
                      }),
                }}
              >
                {currentStatus === AuditorStatusType.APPROVED ? "Remove Approval" : "Mark Approved"}
              </Button>
            </span>
          </Tooltip>

          {/* Red Flag Button */}
          <Tooltip
            title={
              isRedFlagDisabled
                ? (isAuditClosed
                    ? getAuditClosedMessage("Set Red Flag")
                    : currentStatus === AuditorStatusType.APPROVED
                      ? "Cannot set red flag while control is approved"
                      : "Control must be ready for audit before setting red flag")
                : ""
            }
            arrow
          >
            <span>
              <Button
                variant="outlined"
                disabled={isSubmittingRedFlag || isRedFlagDisabled}
                onClick={onRedFlagToggle}
                startIcon={
                  isSubmittingRedFlag ? (
                    <CircularProgress size={16} sx={{ color: "inherit" }} />
                  ) : (
                    <FlagIcon />
                  )
                }
                sx={{
                  borderRadius: 1,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  px: 2.5,
                  py: 0.875,
                  minWidth: 130,
                  ...(redFlag
                    ? {
                        borderColor: "#EF4444",
                        bgcolor: "rgba(239, 68, 68, 0.12)",
                        color: "#DC2626",
                        "&:hover": {
                          borderColor: "#DC2626",
                          bgcolor: "rgba(239, 68, 68, 0.2)",
                        },
                        "&:disabled": {
                          bgcolor: "#D1D5DB !important",
                          color: "#9CA3AF !important",
                          borderColor: "#D1D5DB !important",
                          boxShadow: "none !important",
                        },
                      }
                    : {
                        borderColor: theme.customColors.border.main,
                        color: theme.customColors.text.strong,
                        "&:hover": {
                          borderColor: "#EF4444",
                          bgcolor: "rgba(239, 68, 68, 0.08)",
                        },
                        "&:disabled": {
                          bgcolor: "#D1D5DB !important",
                          borderColor: "#D1D5DB !important",
                          color: "#9CA3AF !important",
                          boxShadow: "none !important",
                        },
                      }),
                }}
              >
                {redFlag ? "Remove Red Flag" : "Set Red Flag"}
              </Button>
            </span>
          </Tooltip>
        </>
      )}

      {/* Ready for Audit Button - Admin Only */}
      {!isAuditorView && (
        <Tooltip
          title={
            isAuditClosed
              ? getAuditClosedMessage("Mark Ready for Audit")
              : currentStatus === AuditorStatusType.APPROVED
                ? "Control has been approved by auditor"
                : currentStatus === AdminStatusType.READY_FOR_AUDIT
                  ? "Already marked as ready for audit"
                  : ""
          }
          arrow
        >
          <span>
            <Button
              variant={currentStatus === AdminStatusType.READY_FOR_AUDIT ? "contained" : "outlined"}
              disabled={isSubmittingStatus || isAuditClosed || currentStatus === AdminStatusType.READY_FOR_AUDIT || currentStatus === AuditorStatusType.APPROVED}
              onClick={onReadyForAuditClick}
              startIcon={
                isSubmittingStatus ? (
                  <CircularProgress size={16} sx={{ color: "inherit" }} />
                ) : (
                  <TaskAltIcon />
                )
              }
              sx={{
                borderRadius: 1,
                textTransform: "none",
                fontSize: "0.875rem",
                fontWeight: 500,
                px: 2.5,
                py: 0.875,
                minWidth: 150,
                ...(currentStatus === AdminStatusType.READY_FOR_AUDIT
                  ? {
                      bgcolor: "#10B981",
                      color: "#fff",
                      "&:hover": {
                        bgcolor: "#059669",
                      },
                      "&:disabled": {
                        bgcolor: "#D1D5DB !important",
                        borderColor: "#D1D5DB !important",
                        color: "#9CA3AF !important",
                        boxShadow: "none !important",
                      },
                    }
                  : {
                      borderColor: theme.customColors.border.main,
                      color: theme.customColors.text.strong,
                      "&:hover": {
                        borderColor: "#10B981",
                        bgcolor: "rgba(16, 185, 129, 0.08)",
                      },
                      "&:disabled": {
                        bgcolor: "#D1D5DB !important",
                        borderColor: "#D1D5DB !important",
                        color: "#9CA3AF !important",
                        boxShadow: "none !important",
                      },
                    }),
              }}
            >
              {currentStatus === AdminStatusType.READY_FOR_AUDIT ? "Ready for Audit" : "Mark Ready for Audit"}
            </Button>
          </span>
        </Tooltip>
      )}

      {/* Evidence Request Button */}
      <Tooltip
        title={
          isAuditClosed
            ? getAuditClosedMessage("Evidence Request")
            : currentStatus === AuditorStatusType.APPROVED
              ? "Cannot request evidence after approval"
              : ""
        }
        arrow
      >
        <span>
          <Button
            startIcon={<AssignmentIcon />}
            variant="contained"
            onClick={onEvidenceRequestClick}
            disabled={isAuditClosed || currentStatus === AuditorStatusType.APPROVED}
            sx={{
              borderRadius: 1,
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              px: 2.5,
              py: 0.875,
              bgcolor: "#3B82F6",
              color: "#fff",
              "&:hover": {
                bgcolor: "#2563EB",
              },
              "&:disabled": {
                bgcolor: "#D1D5DB !important",
                borderColor: "#D1D5DB !important",
                color: "#9CA3AF !important",
                boxShadow: "none !important",
              },
            }}
          >
            Evidence Request
          </Button>
        </span>
      </Tooltip>

      {/* Download Evidence Button */}
      <Tooltip
        title={
          isDownloadingEvidence
            ? "Preparing evidence download..."
            : "Download all evidence for this control"
        }
        arrow
      >
        <span>
          <Button
            startIcon={
              isDownloadingEvidence ? (
                <CircularProgress size={16} sx={{ color: "inherit" }} />
              ) : (
                <DownloadIcon />
              )
            }
            variant="outlined"
            onClick={onDownloadEvidenceClick}
            disabled={isDownloadingEvidence}
            sx={{
              borderRadius: 1,
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              px: 2.5,
              py: 0.875,
              borderColor: theme.customColors.border.main,
              color: theme.customColors.text.strong,
              "&:hover": {
                borderColor: "#3B82F6",
                bgcolor: "rgba(59, 130, 246, 0.08)",
              },
              "&:disabled": {
                bgcolor: "#D1D5DB !important",
                borderColor: "#D1D5DB !important",
                color: "#9CA3AF !important",
                boxShadow: "none !important",
              },
            }}
          >
            {isDownloadingEvidence ? "Preparing..." : "Download Evidence"}
          </Button>
        </span>
      </Tooltip>

      {/* Comments Button */}
      <Box sx={{ position: "relative" }}>
        <Button
          startIcon={<ChatIcon />}
          variant="contained"
          onClick={onCommentsClick}
          sx={{
            borderRadius: 1,
            textTransform: "none",
            fontSize: "0.875rem",
            fontWeight: 500,
            px: 2.5,
            py: 0.875,
            bgcolor: "#3B82F6",
            color: "#fff",
            "&:hover": {
              bgcolor: "#2563EB",
            },
          }}
        >
          Comments
        </Button>
        {commentsCount > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: -6,
              right: -6,
              minWidth: 20,
              height: 20,
              borderRadius: "50%",
              bgcolor: theme.palette.error.main,
              color: theme.customColors.button.text,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.7rem",
              fontWeight: 700,
              boxShadow: "0 2px 6px rgba(239, 68, 68, 0.4)",
              border: `2px solid ${theme.customColors.background.header || "#fff"}`,
            }}
          >
            {commentsCount > 99 ? "99+" : commentsCount}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ControlActionButtons;
